/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { pt_PT } from "../../../../core/pt-PT"
import { isValidIban, loc } from "../../../../core/utils"
import { DATE_FORMAT } from "../../constants/ClientPageConstants"
import genericSchema from "../ClientPageValidator"
import moment from "moment"
import * as yup from "yup"

/* Error messages */
const requiredMessage = () => loc`Required`
const dateYYYYFormatValidationMessage = () => loc`Not a valid YYYY date`
const employmentDateInvalidMessage = () => loc(pt_PT["Not a valid MM/YYYY date"])
const residenceDateInvalidMessage = () => loc(pt_PT["Not a valid YYYY date"])
const invalidBirthDateMessage = () => loc`birthDate is not valid`
const maximumCharactersMessage = (maximumValue) => maximumValue + " " + loc`maximum characters`
const minimumCharactersMessage = (minimalValue) => minimalValue + " " + loc`minimum characters`
const childrenContainsMessage = () => loc`number of children must be contains between 0 and 10`
const postalCodeMessage = () => loc`Invalid postal code`
const ibanWrongFormatMessage = () => loc`Not a valid IBAN`
const futureResidenceDateMessage = () => loc`This date cannot be in future`
const yearSeniorityBeforeBirthDateMessage = () => loc`Year seniority bank cannot be before birth date`
const wrongDiffMessageBankSeniorty = () =>
  loc`difference between bank seniority date and birth date must be at least 16 years`
const numberMessage = () => loc`Must be a number`
const numberIntegerMessage = () => loc`Must be a whole number`
const mustBePositiveMessage = () => loc`Must be positive`
const invalidFormat = () => loc`Invalid format`
const unauthorizedCharactersMessage = () => loc`Unauthorized characters`

const invalideDateErrorMessage = () => loc`Not a valid date`
const birthDateMinMessage = () => loc`Minimum age must be > 18 years old`
const birthDateMaxessage = () => loc`Maximum age must be < 110 years old`

const phoneNumberMessage = () => loc`Invalid phone number`
const emailNotValidMessage = () => loc`Email invalid`
const bicWrongFormatMessage = () => loc`Not a valid BIC`

// regex
const postalCodeRegex = /^[0-9]*[-]{0,1}[0-9]*$/
const taxNumberNIFRegex = /^[0-9]{9}$/
const employmentDateRegex = /^[0-9]{2}\/[0-9]{4}$/
const residenceDateRegex = /^[0-9]{4}$/
const phoneRegex = /^3519[0-9]{8}$/
const emailRegex = /^[a-zA-Z0-9_\-.]+@[a-zA-Z0-9_\-.]+\.[a-zA-Z0-9_\-.]+$/
const ibanStartRegex =
  /^(AT|AD|BE|BG|HR|CY|CZ|DK|EE|FI|FR|DE|GI|GR|HU|IS|IE|IT|LV|LI|LT|LU|MT|MC|NL|NO|PL|PT|RO|SM|SK|SI|ES|SE|CH|GB)/
const defaultRegex =
  /^[a-zA-ZêëéèçÊËÉÈÇàâäÀÂ' ÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ]+(([ -][a-zA-Z êëéèçÊËÉÈÇàâäÀÂÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ])?[a-zA-ZêëéèçÊËÉÈÇàâäÀÂÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ]*)*$/
const bicRegex = /^[A-Z]{6,6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3,3}){0,1}$/

let schema = yup.object().shape({
  identity: yup.object().shape({
    name: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .min(2, minimumCharactersMessage(2))
      .max(32, maximumCharactersMessage(32))
      .matches(defaultRegex, unauthorizedCharactersMessage()),
    firstName: yup
      .string()
      .nullable()
      .min(2, minimumCharactersMessage(2))
      .max(32, maximumCharactersMessage(32))
      .required(requiredMessage)
      .matches(defaultRegex, unauthorizedCharactersMessage()),
    citizenShipCountry: yup.string().nullable().required(requiredMessage),
    taxNumber: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "taxNumber",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identity.taxNumber"
            })
          }
          if (!value?.match(taxNumberNIFRegex)) {
            return this.createError({
              message: invalidFormat,
              path: "identity.taxNumber"
            })
          }
        }
      }),
    birthDate: yup
      .string()
      .nullable()
      .test({
        name: "ageTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identity.birthDate"
            })
          }
          const birthDate = moment(value, DATE_FORMAT.identity.birthDate.BE)
          if (!birthDate.isValid() || birthDate.year() < 1000 || birthDate.year() > 9999) {
            return this.createError({
              message: invalideDateErrorMessage,
              path: "identity.birthDate"
            })
          }
          let currentDate = moment()
          if (!(currentDate.diff(birthDate, "years") >= 18)) {
            return this.createError({
              message: birthDateMinMessage,
              path: "identity.birthDate"
            })
          }
          if (!(currentDate.diff(birthDate, "years") < 110)) {
            return this.createError({
              message: birthDateMaxessage,
              path: "identity.birthDate"
            })
          }
        }
      })
  }),
  coordinates: yup.object().shape({
    postalCode: yup.string().nullable().required(requiredMessage).matches(postalCodeRegex, postalCodeMessage()),
    phoneNumber: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "phone verification",
        message: "",
        test(value) {
          if (!value.replace("+", "").match(phoneRegex)) {
            return this.createError({
              message: phoneNumberMessage,
              path: "coordinates.phoneNumber"
            })
          }
        }
      }),
    email: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "email",
        message: "",
        test(value) {
          if (!value?.match(emailRegex)) {
            return this.createError({
              message: emailNotValidMessage,
              path: "coordinates.email"
            })
          }
        }
      }),
    iban: yup
      .string()
      .nullable()
      .test({
        name: "iban format verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.iban"
            })
          }
          if (!value?.substr(0, 2).toUpperCase().match(ibanStartRegex)) {
            return this.createError({
              message: ibanWrongFormatMessage,
              path: "coordinates.iban"
            })
          }
          // eslint-disable-next-line no-new-wrappers
          let formatedBic = new String(value).toString().split(" ").join("")
          if (!isValidIban(formatedBic)) {
            return this.createError({
              message: ibanWrongFormatMessage,
              path: "coordinates.iban"
            })
          }
        }
      }),
    bic: yup
    .string()
    .nullable()
    .required(requiredMessage)
    .max(11, maximumCharactersMessage(11))
    .test({
      name: "bic format verification",
      message: "",
      test(value) {
        if (value && !value.toUpperCase().match(bicRegex)) {
          return this.createError({
            message: bicWrongFormatMessage,
            path: "coordinates.bic"
          })
        }
      }
    })
    .required(requiredMessage),
    yearBankSeniority: yup
      .string()
      .nullable()
      .test({
        name: "yearbankseniority verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.yearBankSeniority"
            })
          }
          const yearbankseniority = moment(value)
          if (!yearbankseniority.isValid() || yearbankseniority.year() < 1000 || yearbankseniority.year() > 9999) {
            return this.createError({
              message: dateYYYYFormatValidationMessage,
              path: "coordinates.yearBankSeniority"
            })
          }
          // date must not be in future
          if (yearbankseniority.isAfter(moment())) {
            return this.createError({
              message: futureResidenceDateMessage,
              path: "coordinates.yearBankSeniority"
            })
          }

          // birthDate must be valid
          const birthDate = moment(this.options.context.identity.birthDate)
          if (!this.options.context.identity.birthDate) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "coordinates.yearBankSeniority"
            })
          }

          //birthDate must be before bank seniority Date
          if (birthDate.isAfter(yearbankseniority)) {
            return this.createError({
              message: yearSeniorityBeforeBirthDateMessage,
              path: "coordinates.yearBankSeniority"
            })
          }

          //diff between birthDate and bank seniority date must be > 16 years
          const diff = yearbankseniority.year() - birthDate.year()
          if (diff < 16) {
            return this.createError({
              message: wrongDiffMessageBankSeniorty,
              path: "coordinates.yearBankSeniority"
            })
          }
        }
      })
  }),
  situation: yup.object().shape({
    profession: yup.string().nullable().required(requiredMessage),
    employmentDate: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .matches(employmentDateRegex, employmentDateInvalidMessage()),
    residenceDate: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .matches(residenceDateRegex, residenceDateInvalidMessage()),
    dependentChildrenNumber: yup
      .number()
      .nullable()
      .required(requiredMessage)
      .min(0, childrenContainsMessage())
      .max(10, childrenContainsMessage()),
    workingSituation: yup.string().nullable().required(requiredMessage)
  }),
  budget: yup.object().shape({
    netMonthlyIncome: yup.number().nullable().required(requiredMessage),
    numberMonthsIncome: yup.string().nullable().required(requiredMessage),
    otherIncome: yup
      .number(numberMessage)
      .typeError(numberMessage)
      .nullable()
      .integer(numberIntegerMessage)
      .required(requiredMessage)
      .moreThan(-1, mustBePositiveMessage),
    rent: yup
      .number(numberMessage)
      .typeError(numberMessage)
      .nullable()
      .integer(numberIntegerMessage)
      .required(requiredMessage)
      .moreThan(-1, mustBePositiveMessage),
    otherCharges: yup
      .number(numberMessage)
      .typeError(numberMessage)
      .nullable()
      .integer(numberIntegerMessage)
      .required(requiredMessage)
      .moreThan(-1, mustBePositiveMessage)
  })
})

export default (values) => schema.concat(genericSchema).validate(values, { abortEarly: false, context: values })
